import Phaser from 'phaser';
import type { Player } from '../entities/Player';
import { landmark } from '../world/landmarks';
import type { WantedSystem } from './WantedSystem';

export type MissionId = 'ScouseDelivery' | 'LoseTheBizzies' | 'None';

export interface MissionView {
  id: MissionId;
  text: string;
  marker?: { x: number; y: number };
  timerLeft?: number; // seconds
}

export class MissionSystem extends Phaser.Events.EventEmitter {
  private current: MissionId = 'None';
  private state: 'idle' | 'active' | 'success' | 'failed' = 'idle';
  private marker?: Phaser.GameObjects.Sprite;
  private parcelPos?: { x: number; y: number };
  private deadline = 0;
  private hideUntil = 0; // for mission 2
  private text = '';

  constructor(private scene: Phaser.Scene, private player: Player, private wanted: WantedSystem) {
    super();
  }

  start(id: MissionId) {
    this.cleanup();
    this.current = id;
    this.state = 'active';
    if (id === 'ScouseDelivery') {
      const pier = landmark('Pier Head');
      this.parcelPos = { x: pier.x, y: pier.y };
      this.placeMarker(pier.x, pier.y);
      this.text = 'Pick up the parcel at Pier Head.';
    } else if (id === 'LoseTheBizzies') {
      this.wanted.bump(3);
      this.deadline = this.scene.time.now + 60_000; // 60s window
      this.hideUntil = 0;
      this.text = 'Lose the bizzies. Stay clean for 30s.';
    }
    this.emitView();
  }

  private placeMarker(x: number, y: number) {
    if (this.marker) this.marker.destroy();
    this.marker = this.scene.add.sprite(x, y, 'marker').setDepth(10);
    this.scene.tweens.add({ targets: this.marker, scale: 1.4, yoyo: true, repeat: -1, duration: 600 });
  }

  update(now: number) {
    if (this.state !== 'active') return;
    if (this.current === 'ScouseDelivery') {
      const p = this.player.pos();
      if (this.parcelPos) {
        if (Phaser.Math.Distance.Between(p.x, p.y, this.parcelPos.x, this.parcelPos.y) < 30) {
          // picked up parcel; switch to drop-off at Anfield
          const anf = landmark('Anfield');
          this.parcelPos = undefined;
          this.placeMarker(anf.x, anf.y);
          this.text = 'Deliver the parcel to Anfield. Step on it!';
          this.deadline = now + 90_000;
          this.emitView();
        }
      } else {
        const anf = landmark('Anfield');
        if (Phaser.Math.Distance.Between(p.x, p.y, anf.x, anf.y) < 40) {
          this.complete(500);
        } else if (now > this.deadline) {
          this.fail('Too slow, soft lad.');
        } else {
          this.emitView();
        }
      }
    } else if (this.current === 'LoseTheBizzies') {
      if (this.wanted.level === 0) {
        if (this.hideUntil === 0) this.hideUntil = now + 30_000;
        if (now >= this.hideUntil) this.complete(1000);
      } else {
        this.hideUntil = 0;
      }
      if (now > this.deadline) this.fail('They got ya.');
      this.emitView();
    }
  }

  private complete(reward: number) {
    this.player.cash += reward;
    this.text = `Mission complete! +£${reward}`;
    this.state = 'success';
    this.cleanup();
    this.emitView();
    // chain: after first mission, queue second after 4s
    if (this.current === 'ScouseDelivery') {
      this.scene.time.delayedCall(4000, () => this.start('LoseTheBizzies'));
    } else {
      this.scene.time.delayedCall(4000, () => {
        this.current = 'None';
        this.text = 'Free roam. Find more trouble.';
        this.emitView();
      });
    }
  }

  private fail(why: string) {
    this.text = `Mission failed: ${why}`;
    this.state = 'failed';
    this.cleanup();
    this.emitView();
    this.scene.time.delayedCall(4000, () => this.start(this.current));
  }

  private cleanup() {
    if (this.marker) { this.marker.destroy(); this.marker = undefined; }
  }

  private emitView() {
    const view: MissionView = {
      id: this.current,
      text: this.text,
      marker: this.marker ? { x: this.marker.x, y: this.marker.y } : undefined,
      timerLeft: this.deadline ? Math.max(0, (this.deadline - this.scene.time.now) / 1000) : undefined
    };
    this.emit('view', view);
  }
}
